import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { useBrandReport, useBrandTrend } from '../../../../hooks/useVisibility';
import { useFilters, type Granularity } from '../filters';
import { formatDayMonth } from '../../../../utils/dateFormat';
import { CHART_AXIS, CHART_GRID, CHART_TOOLTIP } from '../../../../components/ui/chart';
import { aggregateByGranularity, brandColor } from '../format';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

const MAX_BRANDS = 6;

interface Row { date: string; [brandId: string]: string | number }

export function MultiBrandTrendChart() {
  const { startDate, endDate, granularity, setGranularity } = useFilters();
  const { data: trend, isLoading } = useBrandTrend(startDate, endDate);
  // Use brand report to choose which competitors to include (top 5 by visibility + own).
  const { data: report } = useBrandReport(startDate, endDate, { order_by: 'visibility' });

  const { chartRows, brands } = useMemo(() => {
    const items = trend?.items ?? [];
    if (items.length === 0) return { chartRows: [] as Row[], brands: [] as { id: string; name: string; isOwn: boolean }[] };

    // Pick which brands to plot: own + top-5 by visibility from report.
    const reportRanked = report
      ? report.rows.map((r) => ({
          id: r[report.columns.indexOf('brand_id')] as string,
          isOwn: r[report.columns.indexOf('is_own')] === 1,
          visibility: (r[report.columns.indexOf('visibility')] as number | null) ?? 0,
        }))
      : [];
    const ownId = reportRanked.find((b) => b.isOwn)?.id;
    const topIds = new Set<string>();
    if (ownId) topIds.add(ownId);
    for (const b of reportRanked.sort((a, b) => b.visibility - a.visibility)) {
      if (topIds.size >= MAX_BRANDS) break;
      topIds.add(b.id);
    }

    const filtered = items.filter((b) => topIds.has(b.brandId));
    const dateSet = new Set<string>();
    const brandPoints = new Map<string, Map<string, number>>();
    for (const b of filtered) {
      const points = aggregateByGranularity(
        b.points.map((p) => ({ date: p.date, value: p.visibility })),
        granularity,
      );
      const m = new Map<string, number>();
      for (const p of points) {
        dateSet.add(p.date);
        m.set(p.date, p.value);
      }
      brandPoints.set(b.brandId, m);
    }

    const sortedDates = Array.from(dateSet).sort();
    const rows: Row[] = sortedDates.map((d) => {
      const row: Row = { date: d };
      for (const b of filtered) {
        const v = brandPoints.get(b.brandId)?.get(d);
        if (v != null) row[b.brandId] = Math.round(v * 10) / 10;
      }
      return row;
    });

    return {
      chartRows: rows,
      brands: filtered.map((b) => ({ id: b.brandId, name: b.brandName, isOwn: b.isOwn })),
    };
  }, [trend, report, granularity]);

  return (
    <Card padding="md">
      <CardHeader
        title="Visibility"
        description="How often each brand appears in AI-generated answers"
        action={<GranularityToggle value={granularity} onChange={setGranularity} />}
      />
      {isLoading ? (
        <LoadingState />
      ) : chartRows.length === 0 ? (
        <EmptyState compact title="No trend data yet." />
      ) : (
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="date" {...CHART_AXIS} tickFormatter={(d) => formatDayMonth(String(d))} />
              <YAxis
                {...CHART_AXIS}
                tickFormatter={(v: number) => `${v}%`}
                domain={[0, 'auto']}
              />
              <Tooltip
                {...CHART_TOOLTIP}
                formatter={(value, name) => {
                  const brand = brands.find((b) => b.id === name);
                  return [`${value}%`, brand?.name ?? name];
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={28}
                iconType="line"
                formatter={(value) => brands.find((b) => b.id === value)?.name ?? value}
                wrapperStyle={{ fontSize: 11 }}
              />
              {brands.map((b) => (
                <Line
                  key={b.id}
                  type="linear"
                  dataKey={b.id}
                  stroke={brandColor(b.id, b.isOwn)}
                  strokeWidth={b.isOwn ? 2.5 : 1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function GranularityToggle({ value, onChange }: { value: Granularity; onChange: (g: Granularity) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface-primary p-0.5 text-xs">
      {(['D', 'W', 'M'] as Granularity[]).map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          className={`px-2.5 py-1 rounded-md transition-colors ${
            value === g
              ? 'border border-border font-semibold text-text-primary'
              : 'text-text-tertiary hover:text-text-primary'
          }`}
        >
          {g}
        </button>
      ))}
    </div>
  );
}
